import {
  imArrayMerge,
  imPush,
} from '@client/shared/utils/immutableUtils/immutableUtils';

export const currencyToLocale = {
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  DKK: 'da-DK',
};

export function cleanHttpsLink(httpLink) {
  if (httpLink && httpLink.replace) {
    return httpLink.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0];
  } else {
    return '';
  }
}

export function formatNumber(number, currency) {
  if (typeof number !== 'number') {
    return '-';
  }

  if (currency) {
    const locale = currencyToLocale[currency];

    return `${new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
    }).format(number)}`;
  } else {
    return `${new Intl.NumberFormat('sv-SE').format(number)}`;
  }
}

export function isObjectEmpty(obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
}

export function getFormatedHttpReqError(error) {
  if (error?.data?.errors?.length > 0) {
    return error.data.errors.reduce((accum, error) => {
      return `${accum}${error.field}: ${error.defaultMessage}, `;
    }, '');
  } else if (error?.data?.message) {
    return error?.data?.message;
  } else {
    return error?.toString() || 'ERROR';
  }
}

export function getFormattedDateDDMM(date) {
  const publishDate = new Date(date);
  const day = publishDate.getDate(),
    month = publishDate.getMonth() + 1;

  return `${day}/${month}`;
}

export function monthDyDate(localDate, locale = 'en') {
  const date = new Date(localDate);
  const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(
    date
  );
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(date);

  return `${day} ${month}`.toUpperCase();
}

export function getChunkedArray(array, chunkSize) {
  return array.reduce((accum, item, index) => {
    const isNewChunk = index % chunkSize === 0;
    if (isNewChunk) {
      accum.push([item]);

      return accum;
    } else {
      const currentChunkIndex = accum.length - 1;
      accum[currentChunkIndex].push(item);

      return accum;
    }
  }, []);
}

export function getFormattedNumber(number) {
  return typeof number === 'number' ? number.toLocaleString('sv-SE') : number;
}

export function getObjectWithNullInsteadOfEmptyStrings(values) {
  return Object.keys(values).reduce((acc, objKey) => {
    if (values[objKey] === '') {
      return { ...acc, objKey: null };
    } else return { ...acc, objKey: values[objKey] };
  }, {});
}

export function imSetToArrayDeep(
  array = [],
  mapToAdd = {},
  objUniqueField,
  index
) {
  function findIndexNested(obj, addMap, fields) {
    if (typeof fields === 'string') {
      return obj[fields] === addMap[fields];
    } else if (fields.length === 1) {
      return obj[fields[0]] === addMap[fields[0]];
    } else {
      return findIndexNested(
        obj[fields[0]],
        addMap[fields[0]],
        fields.slice(1)
      );
    }
  }

  const indexToUpdate = index
    ? index
    : array.findIndex((arrayItem) => {
        return findIndexNested(arrayItem, mapToAdd, objUniqueField);
      });

  if (indexToUpdate === -1) {
    // mapToAdd doesn't exists in list
    return imPush(array, mapToAdd);
  } else {
    // mapToAdd obj exists in list. Update old obj with mapToAdd
    const firstPart = imPush(array.slice(0, indexToUpdate), mapToAdd);
    const secondPart = array.slice(indexToUpdate + 1);
    return imArrayMerge(firstPart, secondPart);
  }
}

export function abbreviateNumber(value) {
  if (value == null) return 'N/A';
  let newValue = value;
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  let suffixNum = 0;
  while (newValue >= 1000) {
    newValue /= 1000;
    suffixNum++;
  }

  newValue = +newValue.toPrecision(3);

  newValue += suffixes[suffixNum];
  return newValue;
}

export function formatEngagementRate(engRateDouble) {
  if (!engRateDouble && engRateDouble !== 0) {
    return 'N/A';
  } else {
    return `${engRateDouble}%`;
  }
}

export function isClient() {
  return typeof window === 'object';
}

export function areObjectsEqual(a, b) {
  let aProps = Object.getOwnPropertyNames(a);
  let bProps = Object.getOwnPropertyNames(b);

  if (aProps.length != bProps.length) {
    return false;
  }

  for (let i = 0; i < aProps.length; i++) {
    let propName = aProps[i];
    if (a[propName] !== b[propName]) {
      return false;
    }
  }

  return true;
}

export const fakeEllipsis = (text, maxCharNumber) => {
  if (typeof text === 'string' && text.length > maxCharNumber) {
    return `${text.substring(0, maxCharNumber - 2).trim()}…`;
  } else {
    return text;
  }
};

export const arraysMatch = function(arr1, arr2) {
  if (!arr1 || !arr2) {
    return arr2 === arr1;
  }

  if (arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  return true;
};

export function isNullOrEmpty(value) {
  return value === '' || value === null || value === undefined;
}

export function openPageInNewTab(url) {
  return window.open(decodeURL(url), '_blank');
}

// prefer published images
export function getFirstPossibleImage(media) {
  const firstImage = media.imageCaptionPairs?.find(
    (imageCaptionPair) => imageCaptionPair.imageUrl !== undefined
  );
  if (firstImage) {
    return firstImage.imageUrl;
  }
  return (
    media.imageCaptionPairs[0]?.imageUrl ?? media.imageCaptionPairs[0]?.videoUrl
  );
}

export function getHourAndMins(date) {
  const formattedDate = new Date(date);
  const minutes = formattedDate.getMinutes();
  const hours = formattedDate.getHours();
  const minEnsureDoubleDigit = minutes < 10 ? `0${minutes}` : minutes;
  const hoursEnsureDoubleDigit = hours < 10 ? `0${hours}` : hours;

  return `${hoursEnsureDoubleDigit}:${minEnsureDoubleDigit}`;
}

export function decodeURL(url) {
  if (typeof url !== 'string' || !url.trim()) return null;

  return decodeURIComponent(url.replace(/\s/g, ''));
}
